/**
 * plan 统一表格件（plan-page-design ref 02「表格」、ref 03 §5；PR3 新写，T-08：plan 自建、不改平台组件）。
 * 行高固定（舒适 44 ／ 紧凑 38），表头 12 号；单元格不换行，多标签用 `CellTags` 单行裁切 + `+N`；
 * 放不下时表格自己横向滚动，页面不横滚。plan 页面只许用它出表（S3）。
 */
import type { ReactNode } from "react";

import { Badge, type BadgeTone } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

export type PlanColumn<T> = {
	key: string;
	title: ReactNode;
	render: (row: T) => ReactNode;
	/** 列宽 Tailwind 类（如 `w-40`、`max-w-[240px]`）；不给按内容 */
	className?: string;
	align?: "left" | "right";
};

export function PlanTable<T>({
	columns,
	rows,
	rowKey,
	onRowClick,
	density = "comfortable",
	rowTestId,
	className,
}: {
	columns: PlanColumn<T>[];
	rows: T[];
	rowKey: (row: T) => string;
	onRowClick?: (row: T) => void;
	density?: "comfortable" | "compact";
	rowTestId?: string;
	className?: string;
}) {
	const h = density === "compact" ? "h-[38px]" : "h-11";
	return (
		<div className={cn("overflow-x-auto rounded-lg border border-surface-border", className)}>
			<table className="w-full text-sm">
				<thead className="bg-surface-muted text-xs text-text-secondary">
					<tr className="h-9">
						{columns.map((c) => (
							<th
								key={c.key}
								className={cn(
									"whitespace-nowrap px-3 font-medium",
									c.align === "right" ? "text-right" : "text-left",
									c.className,
								)}
							>
								{c.title}
							</th>
						))}
					</tr>
				</thead>
				<tbody>
					{rows.map((r) => (
						<tr
							key={rowKey(r)}
							data-testid={rowTestId}
							className={cn(
								h,
								"border-t border-surface-border",
								onRowClick ? "cursor-pointer hover:bg-surface-muted/40" : "",
							)}
							onClick={onRowClick ? () => onRowClick(r) : undefined}
							onKeyDown={
								onRowClick
									? (e) => {
											if (e.key === "Enter" || e.key === " ") onRowClick(r);
										}
									: undefined
							}
							tabIndex={onRowClick ? 0 : undefined}
						>
							{columns.map((c) => (
								<td
									key={c.key}
									className={cn(
										"overflow-hidden whitespace-nowrap px-3",
										c.align === "right" ? "text-right" : "text-left",
										c.className,
									)}
								>
									{c.render(r)}
								</td>
							))}
						</tr>
					))}
				</tbody>
			</table>
		</div>
	);
}

/** 单元格多标签：单行，超出 max 个收成 `+N`（悬停看全部） */
export function CellTags({
	items,
	max = 2,
	tone = "muted",
	mono = false,
}: {
	items: string[];
	max?: number;
	tone?: BadgeTone;
	mono?: boolean;
}) {
	if (!items.length) return <span className="text-text-muted">—</span>;
	const shown = items.slice(0, max).map((t, i) => ({ t, k: `${i}:${t}` }));
	const rest = items.length - shown.length;
	return (
		<span className="inline-flex items-center gap-1" title={items.join(" → ")}>
			{shown.map(({ t, k }) => (
				<Badge key={k} tone={tone} className={mono ? "font-mono" : undefined}>
					{t}
				</Badge>
			))}
			{rest > 0 ? <Badge tone="neutral">+{rest}</Badge> : null}
		</span>
	);
}
